import type { FieldOf, FormDefinition } from "@fobu/schema";
import { formatMoney } from "./money-format";

type Amount = FieldOf<"amount">;
type Order = FieldOf<"order">;

const toCents = (n: number) => Math.round(n * 100);

/* ---------------- Betrag ---------------- */

export function amountValue(value: unknown): number | null {
  const n = typeof value === "number" ? value : typeof value === "string" ? Number(value.replace(",", ".")) : NaN;
  return Number.isFinite(n) ? n : null;
}

export function validateAmount(field: Amount, value: unknown): { code: "invalid_number" | "too_small" | "too_large" | "invalid_choice"; message: string; params?: Record<string, number> } | null {
  const n = amountValue(value);
  if (n === null || n < 0) return { code: "invalid_number", message: "Bitte einen gültigen Betrag eingeben." };
  if (Math.abs(n * 100 - Math.round(n * 100)) > 1e-6) return { code: "invalid_number", message: "Höchstens zwei Nachkommastellen." };
  if (!field.allowCustom && !field.presets.some((p) => toCents(p) === toCents(n))) return { code: "invalid_choice", message: "Bitte einen der Beträge wählen." };
  const min = field.min ?? 0.01;
  if (n < min) return { code: "too_small", message: `Mindestens ${String(min).replace(".", ",")}.`, params: { min } };
  if (field.max !== undefined && n > field.max) return { code: "too_large", message: `Höchstens ${String(field.max).replace(".", ",")}.`, params: { max: field.max } };
  return null;
}

/* ---------------- Bestellliste ---------------- */

/** Menge je Artikel (nur gültige, > 0). */
export function orderQuantities(field: Order, value: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!value || typeof value !== "object" || Array.isArray(value)) return out;
  for (const it of field.items) {
    const q = (value as Record<string, unknown>)[it.id];
    if (typeof q === "number" && Number.isInteger(q) && q > 0) out[it.id] = q;
  }
  return out;
}

export const orderTotalCents = (field: Order, value: unknown) => {
  const q = orderQuantities(field, value);
  return field.items.reduce((s, it) => s + toCents(it.price) * (q[it.id] ?? 0), 0);
};

export function validateOrder(field: Order, value: unknown): { code: "invalid_type" | "invalid_choice" | "too_many_items" | "required"; message: string } | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return { code: "invalid_type", message: "Ungültige Bestellung." };
  const ids = new Map(field.items.map((i) => [i.id, i]));
  let total = 0;
  for (const [id, q] of Object.entries(value as Record<string, unknown>)) {
    const it = ids.get(id);
    if (!it) return { code: "invalid_choice", message: "Unbekannter Artikel." };
    if (typeof q !== "number" || !Number.isInteger(q) || q < 0) return { code: "invalid_type", message: "Ungültige Menge." };
    if (q > it.max) return { code: "too_many_items", message: `Von „${it.label}“ höchstens ${it.max}.` };
    total += q;
  }
  if (field.required && total === 0) return { code: "required", message: "Bitte mindestens einen Artikel wählen." };
  return null;
}

export function formatOrder(field: Order, value: unknown, currency = "EUR"): string {
  const q = orderQuantities(field, value);
  const parts = field.items.filter((it) => q[it.id]).map((it) => `${q[it.id]} × ${it.label}`);
  if (!parts.length) return "";
  return `${parts.join(", ")} (${formatMoney(orderTotalCents(field, value), currency)})`;
}

/** Positionen für die Zahlung aus Betrags- und Bestellfeldern (nur sichtbare, mit „in die Zahlung übernehmen“). */
export function fieldPaymentLines(def: FormDefinition, answers: Record<string, unknown>, plain: (s: string) => string) {
  const lines: Array<{ id: string; label: string; unitCents: number; quantity: number; totalCents: number }> = [];
  for (const page of def.pages)
    for (const b of page.blocks) {
      if (b.type === "amount" && b.charge && answers[b.id] !== undefined) {
        const n = amountValue(answers[b.id]);
        if (n !== null && n > 0 && !validateAmount(b, n)) lines.push({ id: `field:${b.id}`, label: plain(b.label) || "Betrag", unitCents: toCents(n), quantity: 1, totalCents: toCents(n) });
      }
      if (b.type === "order" && b.charge && answers[b.id] !== undefined) {
        const q = orderQuantities(b, answers[b.id]);
        for (const it of b.items) {
          const n = q[it.id];
          if (!n || n > it.max) continue;
          lines.push({ id: `order:${b.id}:${it.id}`, label: it.label, unitCents: toCents(it.price), quantity: n, totalCents: toCents(it.price) * n });
        }
      }
    }
  return lines;
}
